import React, { useState, useEffect } from 'react';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { useLocation, Link } from 'react-router-dom';
import { loginRequest } from '../config/MSALConfig';
import { useTranslation } from 'react-i18next';
import { UQNavbar, UQButton, UQCard, type NavbarItemLink } from '@dev/uniq-ui';
import LanguageSwitcher from './LanguageSwitcher';
import logo_dark from '@assets/images/logos/UNQ_Subbrand_365_Negative_Web.svg';
import logo_light from '@assets/images/logos/UNQ_Subbrand_365_Positive_Web.svg';
import logo_cube from '@assets/images/logos/logo-cube.svg';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      const shouldCollapse = window.innerWidth < 1024;
      setIsCollapsed(shouldCollapse);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((error) => {
      console.error('Login error:', error);
    });
  };

  const handleLogout = () => {
    instance.logoutRedirect().catch((error) => {
      console.error('Logout error:', error);
    });
  };

  const links: NavbarItemLink[] = [
    {
      text: t('nav.employeeList'),
      url: '/',
      icon: 'home',
    },
  ];

  const footerLinks: NavbarItemLink[] = [
    {
      text: t('auth.logoutButton'),
      url: '/logout',
      icon: 'log-out',
    },
  ];

  return (
    <>
      <AuthenticatedTemplate>
        <UQNavbar
          location={location}
          links={links}
          logoLight={logo_light}
          logoDark={logo_dark}
          logoLightSmall={logo_cube}
          logoDarkSmall={logo_cube}
          footerLinks={footerLinks}
          singleCollapse={true}
          horizontalCollapsed={isCollapsed}
          content={
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '2rem', overflow: 'hidden' }}>
              <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                <LanguageSwitcher />
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {children}
              </div>
            </div>
          }
          renderLink={(link, children) => {
            if (link.url === '/logout') {
              return (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogout();
                  }}
                  className="navbar-item-base"
                  style={{ cursor: 'pointer' }}
                >
                  {children}
                </a>
              );
            }
            return (
              <Link to={link.url} className="navbar-item-base">
                {children}
              </Link>
            );
          }}
        />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="container" style={{ paddingTop: '3rem', maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <LanguageSwitcher />
          </div>
          <UQCard>
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
                {t('auth.authRequired')}
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
                {t('auth.authRequiredDesc')}
              </p>
              <UQButton onClick={handleLogin} type="primary">
                {t('auth.loginButton')}
              </UQButton>
            </div>
          </UQCard>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
};

export default Layout;